import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import type { BusinessPlan } from '../types/businessPlan';
import { logoColorToHex } from '../types/businessPlan';
import { moderateText } from '../utils/contentModeration';

type Template = 'modern' | 'playful' | 'bold';
type EditorTab = 'edit' | 'preview';

interface ThemePalette {
  name: string;
  bg: string;
  accent: string;
  text: string;
  card: string;
}

const PALETTES: ThemePalette[] = [
  { name: 'Ocean', bg: 'bg-blue-50', accent: 'bg-blue-500', text: 'text-blue-900', card: 'bg-white' },
  { name: 'Forest', bg: 'bg-emerald-50', accent: 'bg-emerald-500', text: 'text-emerald-900', card: 'bg-white' },
  { name: 'Sunset', bg: 'bg-orange-50', accent: 'bg-orange-500', text: 'text-orange-900', card: 'bg-white' },
  { name: 'Berry', bg: 'bg-purple-50', accent: 'bg-purple-500', text: 'text-purple-900', card: 'bg-white' },
  { name: 'Rose', bg: 'bg-pink-50', accent: 'bg-pink-500', text: 'text-pink-900', card: 'bg-white' },
  { name: 'Slate', bg: 'bg-gray-100', accent: 'bg-gray-800', text: 'text-gray-900', card: 'bg-white' },
];

function extractContent(plan: BusinessPlan) {
  const s1 = plan.stageResponses?.ideaExplosion;
  const s2 = plan.stageResponses?.problemSolver;
  const s3 = plan.stageResponses?.customerDetective;
  const s5 = plan.stageResponses?.moneyMoves;
  return {
    businessName: plan.businessName || 'My Business',
    tagline: plan.elevatorPitchSolution || s3?.s3_q2 || 'Building something amazing',
    description: s1?.s1_q2 || '',
    problem: s2?.s2_q1 || '',
    solution: plan.elevatorPitchSolution || '',
    pricing: s5?.s5_q1 || '',
    strategy: s5?.s5_q4 || '',
    logoColor: plan.selectedLogoColors?.[0] ? logoColorToHex(plan.selectedLogoColors[0]) : '#6366f1',
    logoShape: plan.selectedLogoShape || 'square',
  };
}

export default function WebsiteBuilderPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const { plan, loading } = useBusinessPlan(planId);

  const [template, setTemplate] = useState<Template>('modern');
  const [paletteIdx, setPaletteIdx] = useState(0);
  const [tab, setTab] = useState<EditorTab>('edit');
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const content = useMemo(() => plan ? extractContent(plan) : null, [plan]);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const val = (key: string, fallback: string) => edits[key] ?? fallback;
  const setVal = (key: string, v: string) => setEdits((p) => ({ ...p, [key]: v }));

  const palette = PALETTES[paletteIdx];

  const handleSave = async () => {
    if (!user || !profile || !planId) return;

    const newErrors: Record<string, string> = {};
    const fieldsToCheck: Array<{ key: string; fallback: string; isName?: boolean }> = [
      { key: 'businessName', fallback: content?.businessName ?? '', isName: true },
      { key: 'tagline', fallback: content?.tagline ?? '' },
      { key: 'description', fallback: content?.description ?? '' },
      { key: 'problem', fallback: content?.problem ?? '' },
      { key: 'solution', fallback: content?.solution ?? '' },
      { key: 'pricing', fallback: content?.pricing ?? '' },
    ];
    for (const f of fieldsToCheck) {
      const value = val(f.key, f.fallback);
      if (value.trim()) {
        const result = moderateText(value, f.isName);
        if (!result.passed) newErrors[f.key] = result.message ?? 'Content not allowed.';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      const bizRef = doc(db, COLLECTIONS.USERS, user.uid, SUBCOLLECTIONS.PROFILES, profile.id, SUBCOLLECTIONS.BUSINESSES, planId);
      await setDoc(bizRef, { businessPlanId: planId, name: content?.businessName, createdAt: serverTimestamp() }, { merge: true });

      const websiteRef = doc(db, COLLECTIONS.USERS, user.uid, SUBCOLLECTIONS.PROFILES, profile.id, SUBCOLLECTIONS.BUSINESSES, planId, 'websites', 'main');
      await setDoc(websiteRef, { template, palette: PALETTES[paletteIdx].name, content: { ...content, ...edits }, updatedAt: serverTimestamp() }, { merge: true });
      setToastMsg('Website saved!');
      setTimeout(() => setToastMsg(null), 2500);
    } catch (e) {
      console.error(e);
      setToastMsg('Failed to save.');
      setTimeout(() => setToastMsg(null), 2500);
    }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;
  if (!plan || !content) return (
    <AnimatedPage><div className="flex min-h-screen items-center justify-center"><p className="text-gray-500">Plan not found</p></div></AnimatedPage>
  );

  const templateStyles: Record<Template, string> = { modern: 'font-sans', playful: 'font-sans rounded-3xl', bold: 'font-sans uppercase tracking-wide' };

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gray-50 px-4 pb-12 pt-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <button onClick={() => navigate(`/hq/${planId}`)} className="text-sm font-medium text-brand-500 hover:text-brand-600">← Back to HQ</button>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">Website Builder</h1>
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving} className="min-h-[44px] rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => { setToastMsg('Publishing will be available in a future update.'); setTimeout(() => setToastMsg(null), 2500); }} className="min-h-[44px] rounded-xl border-2 border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">Publish</button>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-4">
            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">Template</p>
              <div className="flex gap-2">
                {(['modern', 'playful', 'bold'] as Template[]).map((t) => (
                  <button key={t} onClick={() => setTemplate(t)} className={`min-h-[44px] rounded-lg px-3 py-1 text-sm font-medium capitalize ${template === t ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">Colors</p>
              <div className="flex gap-1">
                {PALETTES.map((p, i) => (
                  <button key={p.name} onClick={() => setPaletteIdx(i)} className={`h-8 w-8 rounded-full ${p.accent} ${paletteIdx === i ? 'ring-2 ring-brand-500 ring-offset-2' : ''}`} title={p.name} />
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4 flex gap-2 md:hidden">
            <button onClick={() => setTab('edit')} className={`min-h-[44px] flex-1 rounded-xl py-2 text-sm font-medium ${tab === 'edit' ? 'bg-brand-500 text-white' : 'bg-white text-gray-600'}`}>Edit</button>
            <button onClick={() => setTab('preview')} className={`min-h-[44px] flex-1 rounded-xl py-2 text-sm font-medium ${tab === 'preview' ? 'bg-brand-500 text-white' : 'bg-white text-gray-600'}`}>Preview</button>
          </div>

          <div className="md:flex md:gap-6">
            <div className={`space-y-4 md:block md:w-1/2 ${tab === 'edit' ? '' : 'hidden'}`}>
              {[
                { key: 'businessName', label: 'Business Name', fallback: content.businessName, type: 'input' as const },
                { key: 'tagline', label: 'Tagline', fallback: content.tagline, type: 'input' as const },
                { key: 'description', label: 'About', fallback: content.description, type: 'textarea' as const },
                { key: 'problem', label: 'Problem We Solve', fallback: content.problem, type: 'textarea' as const },
                { key: 'solution', label: 'Our Solution', fallback: content.solution, type: 'textarea' as const },
                { key: 'pricing', label: 'Pricing', fallback: content.pricing, type: 'input' as const },
                { key: 'contact', label: 'Contact Email', fallback: 'hello@mybusiness.com', type: 'input' as const },
              ].map((f) => (
                <div key={f.key} className="rounded-xl border border-gray-200 bg-white p-4">
                  <label className="mb-1 block text-sm font-medium text-gray-700">{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea rows={3} value={val(f.key, f.fallback)} onChange={(e) => { setVal(f.key, e.target.value); if (fieldErrors[f.key]) setFieldErrors((p) => { const n = { ...p }; delete n[f.key]; return n; }); }} className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none ${fieldErrors[f.key] ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-brand-400'}`} />
                  ) : (
                    <input type="text" value={val(f.key, f.fallback)} onChange={(e) => { setVal(f.key, e.target.value); if (fieldErrors[f.key]) setFieldErrors((p) => { const n = { ...p }; delete n[f.key]; return n; }); }} className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none ${fieldErrors[f.key] ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-brand-400'}`} />
                  )}
                  {fieldErrors[f.key] && <p className="mt-1 text-xs text-red-500">{fieldErrors[f.key]}</p>}
                </div>
              ))}
            </div>

            <div className={`md:block md:w-1/2 ${tab === 'preview' ? '' : 'hidden'}`}>
              <div className={`overflow-hidden rounded-2xl border-2 border-gray-200 ${palette.bg} ${templateStyles[template]}`}>
                <div className={`${palette.accent} px-6 py-12 text-center text-white`}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 text-2xl font-bold" style={{ backgroundColor: content.logoColor }}>
                    {content.logoShape === 'circle' ? '●' : content.logoShape === 'star' ? '★' : content.logoShape === 'heart' ? '♥' : '■'}
                  </div>
                  <h2 className="text-2xl font-bold">{val('businessName', content.businessName)}</h2>
                  <p className="mt-2 text-sm opacity-90">{val('tagline', content.tagline)}</p>
                </div>
                <div className={`${palette.card} p-6`}>
                  <h3 className={`font-bold ${palette.text}`}>About Us</h3>
                  <p className="mt-2 text-sm text-gray-600">{val('description', content.description) || 'Tell us about your business…'}</p>
                </div>
                <div className="grid grid-cols-2 gap-px bg-gray-200">
                  <div className={`${palette.card} p-4`}><h4 className={`text-sm font-bold ${palette.text}`}>The Problem</h4><p className="mt-1 text-xs text-gray-600">{val('problem', content.problem) || '—'}</p></div>
                  <div className={`${palette.card} p-4`}><h4 className={`text-sm font-bold ${palette.text}`}>Our Solution</h4><p className="mt-1 text-xs text-gray-600">{val('solution', content.solution) || '—'}</p></div>
                </div>
                <div className={`${palette.card} p-6 text-center`}>
                  <h3 className={`font-bold ${palette.text}`}>Pricing</h3>
                  <p className="mt-2 text-sm text-gray-600">{val('pricing', content.pricing) || '—'}</p>
                </div>
                <div className={`${palette.accent} p-6 text-center text-white`}>
                  <p className="text-sm font-medium">Get in touch</p>
                  <p className="mt-1 text-xs opacity-80">{val('contact', 'hello@mybusiness.com')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-medium text-white shadow-lg">{toastMsg}</motion.div>
        )}
      </div>
    </AnimatedPage>
  );
}
